// src/rms/data/supabaseAdapter.js
// ============================================================================
// Supabase data adapter
// ============================================================================
// Implements the same repository interface as localAdapter.js against the
// rms_* schema (supabase/migrations/2026081609*, 2026081610*).
//
// Not yet exercised against a live project — the app currently runs on the
// local adapter (VITE_RMS_BACKEND=local). When a ReserveZoo Supabase
// project is attached, verify column names against the live database before
// trusting this file; per CLAUDE.md that verification is mandatory in this repo
// and has caught real bugs before.
// ============================================================================

import { supabase } from "../config/supabaseClient";
import { createSuccessResponse, createErrorResponse } from "../utils/response";

/** Maps the adapter's collection names to physical table names. */
/**
 * PostgREST operators for the `where` shapes the app uses. Bare value is
 * equality, array is IN, object is comparisons -- the same contract
 * localAdapter implements, because a query that means two different things
 * against two backends is worse than a query that works against one.
 *
 * Values are wrapped in double quotes inside an or() filter: PostgREST parses
 * that string itself, and an unquoted ISO timestamp's punctuation is ambiguous
 * to its parser in a way an ordinary .gt() argument is not.
 */
const PG_OPERATORS = { gt: "gt", gte: "gte", lt: "lt", lte: "lte" };

function quoteFilterValue(value) {
  return `"${String(value).replace(/"/g, '\\"')}"`;
}

function applyCondition(query, key, condition) {
  if (Array.isArray(condition)) return query.in(key, condition);
  if (condition === null || typeof condition !== "object") return query.eq(key, condition);

  const comparisons = Object.entries(condition).filter(([op]) => op !== "orNull");
  for (const [op] of comparisons) {
    if (!PG_OPERATORS[op]) throw new Error(`Unknown filter operator: ${op}`);
  }

  // "matches the comparisons, OR is null" has to go through one or() call --
  // chaining .gt() then .or("...is.null") would AND them and defeat the point.
  if (condition.orNull) {
    const clauses = comparisons.map(([op, v]) => `${key}.${PG_OPERATORS[op]}.${quoteFilterValue(v)}`);
    clauses.push(`${key}.is.null`);
    return query.or(clauses.join(","));
  }

  return comparisons.reduce((q, [op, v]) => q[PG_OPERATORS[op]](key, v), query);
}

/**
 * The selected business, remembered per browser.
 *
 * Deliberately not on the user record: two people share a counter terminal on
 * one account in this product, and which business the terminal is showing is a
 * property of the terminal.
 */
const ACTIVE_OPERATOR_KEY = "rms.activeOperator";

function readActiveOperatorId() {
  try {
    return window.localStorage.getItem(ACTIVE_OPERATOR_KEY) || null;
  } catch {
    return null;
  }
}

function writeActiveOperatorId(id) {
  try {
    if (id) window.localStorage.setItem(ACTIVE_OPERATOR_KEY, id);
    else window.localStorage.removeItem(ACTIVE_OPERATOR_KEY);
  } catch {
    /* private mode; the default selection still works */
  }
}

const TABLES = {
  operators: "rms_operators",
  members: "rms_operator_members",
  locations: "rms_locations",
  assetGroups: "rms_asset_groups",
  assets: "rms_assets",
  customers: "rms_customers",
  customerDocuments: "rms_customer_documents",
  reservations: "rms_reservations",
  reservationAddons: "rms_reservation_addons",
  reservationAnswers: "rms_reservation_answers",
  availabilityBlocks: "rms_availability_blocks",
  operatingHours: "rms_operating_hours",
  hoursExceptions: "rms_hours_exceptions",
  addons: "rms_addons",
  rateRules: "rms_rate_rules",
  usageFeeSchedules: "rms_usage_fee_schedules",
  cancellationPolicies: "rms_cancellation_policies",
  checkoutQuestions: "rms_checkout_questions",
  assetNotes: "rms_asset_notes",
  assetDocuments: "rms_asset_documents",
  agreements: "rms_agreements",
  usageReadings: "rms_usage_readings",
  conditionReports: "rms_condition_reports",
  subscriptions: "rms_subscriptions",
  occupancies: "rms_occupancies",
  accessCredentials: "rms_access_credentials",
  accessEvents: "rms_access_events",
  certifications: "rms_customer_certifications",
  deliveries: "rms_deliveries",
  invoices: "rms_invoices",
  payments: "rms_payments",
  taxRates: "rms_tax_rates",
  taxGroups: "rms_tax_groups",
  taxGroupRates: "rms_tax_group_rates",
  taxCategoryDefaults: "rms_tax_category_defaults",
  messageThreads: "rms_message_threads",
  messages: "rms_messages",
  messageTemplates: "rms_message_templates",
  agreementTemplates: "rms_agreement_templates",
  agreementDocuments: "rms_agreement_documents",
  announcements: "rms_announcements",
  feedback: "rms_feedback",
  staffActions: "rms_staff_actions",
  glAccounts: "rms_gl_accounts",
  glEntries: "rms_gl_entries",
  adImpressions: "rms_ad_impressions",
  adClicks: "rms_ad_clicks",
  advertisers: "rms_advertisers",
  insertionOrders: "rms_insertion_orders",
  adCampaigns: "rms_ad_campaigns",
  adCreatives: "rms_ad_creatives",
  adDeliveryDaily: "rms_ad_delivery_daily",
  adRateCard: "rms_ad_rate_card",
};

const tableFor = (collection) => {
  const table = TABLES[collection];
  if (!table) throw new Error(`Unknown RMS collection: ${collection}`);
  return table;
};

export const supabaseAdapter = {
  name: "supabase",

  /**
   * The operator this person works for.
   *
   * The .order() is not cosmetic. This used to be `.limit(1).maybeSingle()`
   * with nothing ordering it, which was harmless only because no user could
   * belong to two businesses -- staff invitations did not work, so everybody
   * had exactly one. The moment acceptance works, somebody can own one
   * business and be staff at another, and an unordered LIMIT 1 hands them a
   * different one on different page loads.
   *
   * Oldest first, so the answer is stable and is the business they have had
   * longest. A real operator switcher is follow-up; being deterministic is not.
   */
  /**
   * Every business this user can reach.
   *
   * RLS does the filtering -- the policy on rms_operators is
   * `user_id = auth.uid() OR rms_is_operator_member(id)` -- so this returns
   * exactly what the person is entitled to and nothing else. Ordered oldest
   * first so the default selection is stable across sessions.
   */
  async listOperatorsForUser() {
    const { data: userResult } = await supabase.auth.getUser();
    if (!userResult?.user?.id) return createSuccessResponse([]);

    const { data, error } = await supabase
      .from(TABLES.operators)
      .select("*")
      .order("created_at", { ascending: true });

    if (error) return createErrorResponse(error.message);
    return createSuccessResponse(data || []);
  },

  /**
   * Which business this session is looking at.
   *
   * Stored client-side because it is a preference, not a permission: it can
   * only ever select from what RLS already returned, so a tampered value
   * selects nothing rather than somebody else's business.
   *
   * This used to take the oldest operator unconditionally, which was
   * invisible while every user had exactly one and became a real defect the
   * moment somebody had two -- the second was unreachable through the UI,
   * permanently, with nothing on screen saying it existed.
   */
  async getOperatorForUser() {
    const listed = await this.listOperatorsForUser();
    if (!listed.success) return listed;

    const operators = listed.data || [];
    if (!operators.length) return createSuccessResponse(null);

    const preferred = readActiveOperatorId();
    const chosen = operators.find((o) => o.id === preferred) || operators[0];

    // Clear a stale preference rather than leave it pointing at a business
    // this account was removed from, which would otherwise re-fail every load.
    if (preferred && chosen.id !== preferred) writeActiveOperatorId(chosen.id);

    return createSuccessResponse(chosen);
  },

  async setActiveOperator(operatorId) {
    writeActiveOperatorId(operatorId);
    return createSuccessResponse(operatorId);
  },

  async createOperator(payload) {
    const { data: userResult } = await supabase.auth.getUser();
    const userId = userResult?.user?.id;
    if (!userId) return createErrorResponse("You must be signed in to create an account.");

    const { data, error } = await supabase
      .from(TABLES.operators)
      .insert({
        user_id: userId,
        business_name: payload.business_name,
        vertical_key: payload.vertical_key,
        config_overrides: payload.config_overrides || {},
        timezone: payload.timezone || "UTC",
        currency: payload.currency || "USD",
        country: payload.country || null,
        default_locale: payload.default_locale || "en",
        prices_include_tax: !!payload.prices_include_tax,
      })
      .select()
      .single();

    if (error) return createErrorResponse(error.message);
    return createSuccessResponse(data);
  },

  /**
   * The signed-in person's own member row, with their role.
   *
   * Falls back to a synthesised owner when the row is missing but the operator
   * is theirs. That mirrors rms_operator_role(), which resolves
   * rms_operators.user_id before consulting the roster and for the same
   * reason: the founding owner must never be locked out of their own business
   * by a member row that is absent, malformed, or not yet backfilled.
   */
  async getCurrentMember(operatorId) {
    const { data: userResult } = await supabase.auth.getUser();
    const userId = userResult?.user?.id;
    if (!userId || !operatorId) return createSuccessResponse(null);

    const { data, error } = await supabase
      .from(TABLES.members)
      .select("*")
      .eq("operator_id", operatorId)
      .eq("user_id", userId)
      .eq("status", "active")
      .maybeSingle();

    if (error) return createErrorResponse(error.message);
    if (data) return createSuccessResponse(data);

    const { data: operator } = await supabase
      .from(TABLES.operators)
      .select("user_id")
      .eq("id", operatorId)
      .maybeSingle();

    if (operator?.user_id === userId) {
      return createSuccessResponse({ operator_id: operatorId, user_id: userId, role: "owner", status: "active" });
    }
    return createSuccessResponse(null);
  },

  /** No such thing off the local backend; the role is whatever the row says. */
  async setViewAsRole() {
    return createSuccessResponse(null);
  },

  async updateOperator(operatorId, patch) {
    const { data, error } = await supabase
      .from(TABLES.operators)
      .update(patch)
      .eq("id", operatorId)
      .select()
      .single();

    if (error) return createErrorResponse(error.message);
    return createSuccessResponse(data);
  },

  async list(collection, where = {}, { orderBy, direction = "asc" } = {}) {
    let query = supabase.from(tableFor(collection)).select("*");
    Object.entries(where).forEach(([key, value]) => {
      query = applyCondition(query, key, value);
    });
    if (orderBy) query = query.order(orderBy, { ascending: direction !== "desc" });

    const { data, error } = await query;
    if (error) return createErrorResponse(error.message);
    return createSuccessResponse(data || []);
  },

  async get(collection, id) {
    const { data, error } = await supabase
      .from(tableFor(collection))
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error) return createErrorResponse(error.message);
    return createSuccessResponse(data);
  },

  async create(collection, row) {
    const { data, error } = await supabase
      .from(tableFor(collection))
      .insert(row)
      .select()
      .single();

    if (error) return createErrorResponse(error.message);
    return createSuccessResponse(data);
  },

  async update(collection, id, patch) {
    const { data, error } = await supabase
      .from(tableFor(collection))
      .update(patch)
      .eq("id", id)
      .select()
      .single();

    if (error) return createErrorResponse(error.message);
    return createSuccessResponse(data);
  },

  async remove(collection, id) {
    const { error } = await supabase.from(tableFor(collection)).delete().eq("id", id);
    if (error) return createErrorResponse(error.message);
    return createSuccessResponse(true);
  },

  // The database enforces both of these; these client-side checks exist to give
  // a useful message before the round trip rather than surfacing a raw
  // constraint violation.
  async findConflictingReservation(operatorId, assetId, startsAt, endsAt, excludeId = null) {
    let query = supabase
      .from(TABLES.reservations)
      .select("*")
      .eq("asset_id", assetId)
      .in("status", ["pending", "confirmed", "checked_out", "active"])
      .lt("starts_at", endsAt || "infinity");

    if (excludeId) query = query.neq("id", excludeId);

    const { data } = await query;
    return (data || []).find((r) => !r.ends_at || new Date(r.ends_at) > new Date(startsAt)) || null;
  },

  async hasValidCertification(operatorId, customerId, certificationType) {
    // "none" is the stored value on required_certification meaning the machine
    // needs no ticket at all. It is a preset option value, not display text.
    if (!certificationType || certificationType === "none") return true;
    const { data, error } = await supabase.rpc("rms_customer_has_valid_certification", {
      p_customer_id: customerId,
      p_certification_type: certificationType,
    });
    if (error) return false;
    return !!data;
  },

  async nextNumber(collection, operatorId, prefix, field) {
    // Postgres has no sequence per operator; derive from the highest existing
    // number. Race-prone under heavy concurrency — move to a DB function
    // (SELECT ... FOR UPDATE on a counters table) before multi-user launch.
    const { data } = await supabase
      .from(tableFor(collection))
      .select(field)
      .eq("operator_id", operatorId)
      .order(field, { ascending: false })
      .limit(1);

    const latest = data?.[0]?.[field] || "";
    const match = String(latest).match(/(\d+)$/);
    const next = match ? parseInt(match[1], 10) + 1 : 1;
    return `${prefix}-${String(next).padStart(4, "0")}`;
  },
};

export default supabaseAdapter;
